import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { useToast } from "@/components/ui/use-toast";
import { Settings } from "lucide-react";
import { formatPhoneNumber } from "@/lib/phoneUtils";

interface Employee {
  _id: Id<"employees">;
  name: string;
  avatar?: string;
  position: string;
  department: string;
  email: string;
  phone: string;
  hireDate: string;
  status: "active" | "inactive";
}

interface EditEmployeeSheetProps {
  isOpen: boolean;
  onClose: () => void;
  employee: Employee | null;
  onSave?: () => void;
}

export function EditEmployeeSheet({ isOpen, onClose, employee, onSave }: EditEmployeeSheetProps) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    department: "",
    hireDate: "",
    status: "active" as "active" | "inactive",
  });
  const { toast } = useToast();

  const updateEmployee = useMutation(api.employees.update);
  const positions = useQuery(api.positions.listEnabled);

  // Populate form when employee data changes
  useEffect(() => {
    if (employee) {
      setFormData({
        name: employee.name,
        email: employee.email,
        phone: employee.phone,
        position: employee.position,
        department: employee.department,
        hireDate: employee.hireDate,
        status: employee.status,
      });
    }
  }, [employee]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setFormData((prev) => ({ ...prev, phone: formattedPhone }));
  };

  const handleSubmit = async () => {
    if (!employee) {
      toast({
        title: "Error",
        description: "No employee selected for editing",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateEmployee({
        id: employee._id,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        department: formData.department,
        hireDate: formData.hireDate,
        status: formData.status,
      });

      toast({
        title: "Success!",
        description: "Employee updated successfully",
      });
      onSave?.();
      onClose();
    } catch (error) {
      console.error("Failed to update employee:", error);
      toast({
        title: "Error",
        description: "Failed to update employee",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    // Reset form to original employee data when closing
    if (employee) {
      setFormData({
        name: employee.name,
        email: employee.email,
        phone: employee.phone,
        position: employee.position,
        department: employee.department,
        hireDate: employee.hireDate,
        status: employee.status,
      });
    }
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Edit Employee</SheetTitle>
          <SheetDescription>
            Update the details for {employee?.name || "this employee"}.
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="name">
                Full Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                placeholder="Enter employee name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="position">
                    Position <span className="text-red-500">*</span>
                  </Label>
                  {positions && positions.length === 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                      onClick={() => {
                        onClose();
                        navigate("/settings?tab=positions");
                      }}
                    >
                      <Settings className="mr-1 h-3 w-3" />
                      Configure Positions
                    </Button>
                  )}
                </div>
                <Select
                  value={positions && positions.length === 0 ? "" : formData.position}
                  onValueChange={(value) => handleInputChange("position", value)}
                  disabled={positions === undefined}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        positions === undefined
                          ? "Loading positions..."
                          : positions.length === 0
                            ? "No positions configured"
                            : "Select position"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {positions && positions.length > 0 ? (
                      positions.map((position: any) => (
                        <SelectItem key={position._id} value={position.name}>
                          {position.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="__no_positions__" disabled>
                        No positions available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">
                  Department <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.department}
                  onValueChange={(value) => handleInputChange("department", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Operations">Operations</SelectItem>
                    <SelectItem value="Cleaning Staff">Cleaning Staff</SelectItem>
                    <SelectItem value="Human Resources">Human Resources</SelectItem>
                    <SelectItem value="Sales">Sales</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                    <SelectItem value="Management">Management</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">
                  Phone <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone"
                  placeholder="(555) 555-5555"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  maxLength={14}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hire-date">
                Hire Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="hire-date"
                type="date"
                value={formData.hireDate}
                onChange={(e) => handleInputChange("hireDate", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange("status", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleSubmit}
                disabled={
                  !formData.name ||
                  !formData.email ||
                  !formData.phone ||
                  !formData.position ||
                  !formData.department ||
                  !formData.hireDate
                }
              >
                Update Employee
              </Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
